import { isEqual } from 'lodash-es';
import { setData } from 'platform/forms-system/src/js/actions';
import { toggleValues } from 'platform/site-wide/feature-toggles/selectors';
import { loadingTogglesSelector } from 'platform/utilities/feature-toggles/useFeatureToggle';
import { useEffect } from 'react';
import { shallowEqual, useDispatch, useSelector, useStore } from 'react-redux';
import { getToggleFormFields } from '../utils/formData';

/**
 * Seeds the form data with view fields derived from feature toggles, plus any
 * additional fields the app provides, in a single `setData` dispatch.
 *
 * Toggle values are booleans. Nothing is dispatched while toggles are loading
 * or `skip` is true, or when form data already holds every field. Fields are
 * re-applied whenever `form.data` is replaced, such as after an in-progress
 * form loads.
 *
 * @example
 * const FORM_TOGGLES = [
 *   { toggleName: 'hcaRegOnlyEnabled', formKey: 'view:isRegOnlyEnabled' },
 * ];
 * useDefaultFormData(FORM_TOGGLES, {
 *   extraFields: { 'view:isLoggedIn': isLoggedIn },
 *   skip: profileLoading,
 * });
 *
 * @param {{ toggleName: string, formKey: string }[]} toggles - toggle configs;
 * define outside the component so the reference stays stable
 * @param {Object} [options]
 * @param {Object} [options.extraFields] - additional view fields merged into
 * the same dispatch
 * @param {boolean} [options.skip] - defer seeding until `false`
 * @returns {void}
 */
export const useDefaultFormData = (
  toggles,
  { extraFields = {}, skip = false } = {},
) => {
  const dispatch = useDispatch();
  const store = useStore();

  const formData = useSelector(state => state.form.data);
  const loading = useSelector(loadingTogglesSelector);
  const toggleFields = useSelector(
    state => getToggleFormFields(toggleValues(state), toggles),
    shallowEqual,
  );

  useEffect(() => {
    if (loading || skip) return;

    const latestData = store.getState().form.data;
    const viewFields = { ...toggleFields, ...extraFields };
    const updates = Object.fromEntries(
      Object.entries(viewFields).filter(
        ([key, value]) => !isEqual(latestData[key], value),
      ),
    );

    if (Object.keys(updates).length > 0) {
      dispatch(setData({ ...latestData, ...updates }));
    }
  }, [dispatch, store, loading, skip, formData, toggleFields, extraFields]);
};
